import { makeAutoObservable, runInAction } from 'mobx';
import { nextTempCarRef, nextTempDetayRef, resolveTempRefInModel } from '../utils/tempIdUtils';

/**
 * Global Cari Model Store
 * Her açılan cari kartı için ayrı model slot'u (carKey = CAR_REF veya temp ref)
 * UI lokalde bu model üzerinde çalışır, sync engine arkada server'a gönderir
 */
class CariModelStore {
  constructor() {
    // carKey -> { carikart, cariAramaList, meta }
    this.models = new Map();
    this.listeners = new Map();
    makeAutoObservable(this, {
      models: false,
      listeners: false,
    });
  }

  /**
   * Cari modelini getir veya yoksa null
   */
  getModel(carKey) {
    return this.models.get(carKey) ?? null;
  }

  /**
   * Cari modelini ayarla (load veya init)
   */
  setModel(carKey, data) {
    runInAction(() => {
      const existing = this.models.get(carKey) || {};
      const model = {
        carikart: data.carikart ?? existing.carikart ?? {},
        cariAramaList: Array.isArray(data.cariAramaList) ? data.cariAramaList : (existing.cariAramaList || []),
        meta: {
          changed: existing.meta?.changed ?? false,
          loading: false,
          syncError: null,
          ...data.meta,
        },
      };
      this.models.set(carKey, model);
      this._notify(carKey, 'model');
    });
    return this.models.get(carKey);
  }

  /**
   * Yeni cari oluştur (temp ID ile)
   */
  createNewCari(initialData = {}) {
    const tempCarRef = nextTempCarRef();
    const model = {
      carikart: { CAR_REF: tempCarRef, ...initialData },
      cariAramaList: [],
      meta: { changed: false, loading: false, syncError: null, isNew: true },
    };
    runInAction(() => {
      this.models.set(tempCarRef, model);
      this._notify(tempCarRef, 'model');
    });
    return tempCarRef;
  }

  /**
   * Cari kart ana alanlarını güncelle
   */
  updateCarikart(carKey, patch) {
    const model = this.models.get(carKey);
    if (!model) return;
    runInAction(() => {
      model.carikart = { ...model.carikart, ...patch };
      model.meta.changed = true;
      this._notify(carKey, 'carikart');
    });
  }

  /**
   * Arama listesini güncelle
   */
  setCariAramaList(carKey, list) {
    const model = this.models.get(carKey);
    if (!model) return;
    runInAction(() => {
      model.cariAramaList = Array.isArray(list) ? [...list] : [];
      model.meta.changed = true;
      this._notify(carKey, 'cariAramaList');
    });
  }

  /**
   * Arama listesine ekle veya güncelle
   */
  upsertArama(carKey, arama) {
    const model = this.models.get(carKey);
    if (!model) return;
    runInAction(() => {
      const ref = arama.CD_REF ?? arama.cd_ref;
      const idx = model.cariAramaList.findIndex((r) => (r.CD_REF ?? r.cd_ref) === ref);
      const arr = [...model.cariAramaList];
      if (idx >= 0) {
        arr[idx] = { ...arr[idx], ...arama };
      } else {
        arr.unshift(arama);
      }
      model.cariAramaList = arr;
      model.meta.changed = true;
      this._notify(carKey, 'cariAramaList');
    });
  }

  /**
   * Arama listesinden sil
   */
  removeArama(carKey, cdRef) {
    const model = this.models.get(carKey);
    if (!model) return;
    runInAction(() => {
      model.cariAramaList = model.cariAramaList.filter((r) => (r.CD_REF ?? r.cd_ref) !== cdRef);
      model.meta.changed = true;
      this._notify(carKey, 'cariAramaList');
    });
  }

  /**
   * FormData benzeri obje döndür (DevExtreme Form binding için)
   */
  getFormData(carKey) {
    const model = this.models.get(carKey);
    if (!model) return {};
    return {
      ...model.carikart,
      cariAramaList: model.cariAramaList,
    };
  }

  /**
   * Değişiklik bayrağını sıfırla
   */
  clearChanged(carKey) {
    const model = this.models.get(carKey);
    if (model) {
      runInAction(() => {
        model.meta.changed = false;
        model.meta.syncError = null;
      });
    }
  }

  /**
   * Geçici ID'yi gerçek ID ile değiştir (sync sonrası)
   */
  resolveTempId(carKey, tempIdVal, realId) {
    if (carKey === tempIdVal) {
      runInAction(() => {
        const m = this.models.get(tempIdVal);
        if (m) {
          this.models.delete(tempIdVal);
          resolveTempRefInModel(m, tempIdVal, realId);
          m.carikart = { ...m.carikart, CAR_REF: realId };
          this.models.set(realId, m);
          this._notify(realId, 'model');
        }
      });
    } else {
      const model = this.models.get(carKey);
      if (model) {
        resolveTempRefInModel(model, tempIdVal, realId);
        this._notify(carKey, 'model');
      }
    }
  }

  /**
   * Modeli sil (kart kapatıldığında)
   */
  removeModel(carKey) {
    runInAction(() => {
      this.models.delete(carKey);
      this._notify(carKey, 'removed');
    });
  }

  subscribe(carKey, callback) {
    const key = String(carKey);
    if (!this.listeners.has(key)) this.listeners.set(key, []);
    this.listeners.get(key).push(callback);
    return () => {
      const arr = this.listeners.get(key);
      if (arr) {
        const i = arr.indexOf(callback);
        if (i >= 0) arr.splice(i, 1);
      }
    };
  }

  _notify(carKey, event) {
    const key = String(carKey);
    const arr = this.listeners.get(key);
    if (arr) {
      arr.forEach((cb) => {
        try {
          cb(event, this.getModel(carKey));
        } catch (e) {
          console.error('CariModelStore listener error:', e);
        }
      });
    }
  }
}

const cariModelStore = new CariModelStore();
export default cariModelStore;
